import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { QRCodeSVG } from 'qrcode.react';

const CheckInPage = () => {
    const [todayBookings, setTodayBookings] = useState([]);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [qrData, setQrData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [qrLoading, setQrLoading] = useState(false);

    const fetchTodayBookings = useCallback(async () => {
        try {
            const res = await api.get('/checkin/my-today');
            setTodayBookings(res.data);
            setLoadError(false);
        } catch (error) {
            // Antes ficava só no console: o atleta via «Sem reservas para hoje» e ia-se embora sem
            // fazer check-in, apesar de ter aula marcada.
            console.error('Erro:', error);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTodayBookings();
    }, [fetchTodayBookings]);

    const generateQr = async (booking) => {
        setQrLoading(true);
        try {
            const res = await api.get(`/checkin/qr/${booking.id}`);
            setQrData(res.data);
            setSelectedBooking(booking);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erro ao gerar QR Code');
        } finally {
            setQrLoading(false);
        }
    };

    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString('pt-PT', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner}></div>
                <p>A carregar...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>📱 Check-in</h1>
                <p style={styles.subtitle}>Apresente o QR Code ao staff para fazer check-in</p>
            </div>

            {/* QR Code Modal */}
            {qrData && (
                <div style={styles.qrModal} onClick={() => { setQrData(null); setSelectedBooking(null); }}>
                    <div style={styles.qrCard} onClick={e => e.stopPropagation()}>
                        <button 
                            style={styles.closeBtn}
                            onClick={() => { setQrData(null); setSelectedBooking(null); }}
                        >
                            ✕
                        </button>

                        <div style={styles.qrHeader}>
                            <h2 style={styles.qrTitle}>Check-in QR Code</h2>
                            <p style={styles.qrSubtitle}>
                                {selectedBooking?.timeSlot?.name}
                            </p>
                            <p style={styles.qrTime}>
                                {selectedBooking?.timeSlot?.startTime && formatTime(selectedBooking.timeSlot.startTime)}
                            </p>
                        </div>

                        <div style={styles.qrContainer}>
                            <QRCodeSVG 
                                value={qrData.qrCode}
                                size={250}
                                level="H"
                                includeMargin={true}
                                style={styles.qrCode}
                            />
                        </div>

                        <div style={styles.qrFooter}>
                            <p style={styles.expiryText}>
                                Válido até: {new Date(qrData.expiresAt).toLocaleTimeString('pt-PT', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </p>
                            <p style={styles.instructions}>
                                Apresente este código ao staff do ginásio
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Today's Bookings */}
            <div style={styles.content}>
                <h2 style={styles.sectionTitle}>Reservas de Hoje</h2>

                {loadError ? (
                    <div style={styles.emptyState} role="alert">
                        <span style={styles.emptyIcon}>⚠️</span>
                        <h3>Não foi possível carregar as reservas de hoje</h3>
                        <p>A ligação à API falhou — isto não quer dizer que não tenha aula marcada.</p>
                        <button type="button" onClick={fetchTodayBookings} style={styles.retryButton}>Tentar de novo</button>
                    </div>
                ) : todayBookings.length === 0 ? (
                    <div style={styles.emptyState}>
                        <span style={styles.emptyIcon}>📅</span>
                        <h3>Sem reservas para hoje</h3>
                        <p>Faça uma reserva para poder fazer check-in</p>
                    </div>
                ) : (
                    <div style={styles.bookingsList}>
                        {todayBookings.map(booking => (
                            <div key={booking.id} style={styles.bookingCard}>
                                <div style={styles.bookingTime}>
                                    <span style={styles.timeText}>
                                        {formatTime(booking.timeSlot.startTime)}
                                    </span>
                                    <span style={styles.timeSeparator}>-</span>
                                    <span style={styles.timeText}>
                                        {formatTime(booking.timeSlot.endTime)}
                                    </span>
                                </div>

                                <div style={styles.bookingInfo}>
                                    <h3 style={styles.bookingName}>{booking.timeSlot.name}</h3>
                                </div>

                                <div style={styles.bookingActions}>
                                    {booking.checkedIn ? (
                                        <div style={styles.checkedInBadge}>
                                            <span style={styles.checkIcon}>✓</span>
                                            <span>Check-in feito</span>
                                            <span style={styles.checkedInTime}>
                                                {booking.checkedInAt && formatTime(booking.checkedInAt)}
                                            </span>
                                        </div>
                                    ) : booking.canCheckIn ? (
                                        <button 
                                            style={styles.qrButton}
                                            onClick={() => generateQr(booking)}
                                            disabled={qrLoading}
                                        >
                                            {qrLoading ? 'A gerar...' : '📱 Ver QR Code'}
                                        </button>
                                    ) : (
                                        <div style={styles.waitingBadge}>
                                            <span>⏰ Aguardar</span>
                                            <span style={styles.waitingNote}>
                                                Check-in disponível 15 min antes
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Instructions */}
                <div style={styles.instructionsCard}>
                    <h3 style={styles.instructionsTitle}>ℹ️ Como funciona</h3>
                    <ol style={styles.instructionsList}>
                        <li>O check-in fica disponível <strong>15 minutos antes</strong> da aula</li>
                        <li>Clique em <strong>"Ver QR Code"</strong> para gerar o seu código</li>
                        <li>Apresente o QR Code ao staff do ginásio</li>
                        <li>O staff vai escanear e confirmar o seu check-in</li>
                    </ol>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: 'var(--info-bg)',
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: '1rem',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid var(--info)',
        borderTop: '4px solid var(--accent-primary)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    header: {
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        padding: '2rem',
        color: 'white',
        textAlign: 'center',
    },
    title: {
        margin: 0,
        fontSize: '1.75rem',
    },
    subtitle: {
        margin: '0.5rem 0 0 0',
        opacity: 0.9,
    },
    content: {
        padding: '1.5rem',
        maxWidth: '600px',
        margin: '0 auto',
    },
    sectionTitle: {
        margin: '0 0 1rem 0',
        fontSize: '1.25rem',
        color: 'var(--info-text)',
    },
    emptyState: {
        backgroundColor: 'var(--bg-card)',
        borderRadius: '16px',
        padding: '3rem 2rem',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    retryButton: {
        marginTop: '1rem',
        padding: '10px 16px',
        borderRadius: '10px',
        border: '1px solid var(--border-color)',
        background: 'var(--bg-card)',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        fontSize: '14px',
    },
    emptyIcon: {
        fontSize: '3rem',
        display: 'block',
        marginBottom: '1rem',
    },
    bookingsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
    },
    bookingCard: {
        backgroundColor: 'var(--bg-card)',
        borderRadius: '16px',
        padding: '1.25rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    },
    bookingTime: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '0.75rem',
    },
    timeText: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: 'var(--info-text)',
    },
    timeSeparator: {
        color: 'var(--info)',
    },
    bookingInfo: {
        marginBottom: '1rem',
    },
    bookingName: {
        margin: 0,
        fontSize: '1.125rem',
        color: 'var(--info-text)',
    },
    bookingActions: {},
    checkedInBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        backgroundColor: 'var(--success-bg)',
        color: 'var(--success-text)',
        padding: '0.75rem 1rem',
        borderRadius: '12px',
        fontWeight: '600',
    },
    checkIcon: {
        fontSize: '1.25rem',
    },
    checkedInTime: {
        marginLeft: 'auto',
        fontSize: '0.875rem',
        opacity: 0.8,
    },
    waitingBadge: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--warning-bg)',
        color: 'var(--warning-text)',
        padding: '0.75rem 1rem',
        borderRadius: '12px',
    },
    waitingNote: {
        fontSize: '0.75rem',
        opacity: 0.8,
    },
    qrButton: {
        width: '100%',
        padding: '0.875rem 1.5rem',
        backgroundColor: 'var(--accent-primary)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    qrModal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
    },
    qrCard: {
        backgroundColor: 'var(--bg-card)',
        borderRadius: '24px',
        padding: '2rem',
        width: '100%',
        maxWidth: '380px',
        position: 'relative',
        textAlign: 'center',
    },
    closeBtn: {
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        border: 'none',
        backgroundColor: 'var(--info-bg)',
        cursor: 'pointer',
        fontSize: '1rem',
    },
    qrHeader: {
        marginBottom: '1.5rem',
    },
    qrTitle: {
        margin: '0 0 0.5rem 0',
        fontSize: '1.25rem',
        color: 'var(--info-text)',
    },
    qrSubtitle: {
        margin: 0,
        color: 'var(--info)',
    },
    qrTime: {
        margin: '0.25rem 0 0 0',
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: 'var(--accent-primary)',
    },
    qrContainer: {
        backgroundColor: 'var(--bg-card)',
        padding: '1rem',
        borderRadius: '16px',
        display: 'inline-block',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    },
    qrCode: {
        display: 'block',
    },
    qrFooter: {
        marginTop: '1.5rem',
    },
    expiryText: {
        margin: '0 0 0.5rem 0',
        color: 'var(--warning)',
        fontWeight: '600',
    },
    instructions: {
        margin: 0,
        color: 'var(--info)',
        fontSize: '0.875rem',
    },
    instructionsCard: {
        backgroundColor: 'var(--primary-light)',
        borderRadius: '16px',
        padding: '1.5rem',
        marginTop: '2rem',
    },
    instructionsTitle: {
        margin: '0 0 1rem 0',
        color: 'var(--accent-primary)',
    },
    instructionsList: {
        margin: 0,
        paddingLeft: '1.25rem',
        color: 'var(--info-text)',
        lineHeight: 1.8,
    },
};

export default CheckInPage;
